/**
 * Group parse notices under the file they came from, keeping first-seen file
 * order so the panel reflects the order the files were dropped.
 *
 * A single upload used to repeat its own filename on every line, which reads as
 * several uploads rather than one file with several notes.
 */
export function groupNoticesByFile<T extends { fileName: string }>(notices: T[]): Array<[string, T[]]> {
  const groups: Array<[string, T[]]> = []
  for (const notice of notices) {
    const existing = groups.find(([name]) => name === notice.fileName)
    if (existing) existing[1].push(notice)
    else groups.push([notice.fileName, [notice]])
  }
  return groups
}

/**
 * Whether the sheet name still needs showing as a prefix.
 *
 * Parser messages are required to name their own sheet — there is an acceptance
 * test for it, because the message travels into logs and exports without the
 * structured field. So rendering the prefix as well reads as a stutter:
 * `sheet "Data" — Sheet "Data": a total row…`. Suppress it when the message
 * already opens with the sheet name; notices from the column dictionary do not,
 * and still get the prefix.
 */
export function needsSheetPrefix(notice: { sheet?: string; message: string }): boolean {
  if (!notice.sheet) return false
  return !notice.message.trimStart().toLowerCase().startsWith(`sheet "${notice.sheet.toLowerCase()}"`)
}
